import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";
import { connect } from "react-redux";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import Icon from "@insite/mobius/Icon";
import GearIcon from "@MasterBluePrint/Icons/GearIcon";

const enum fields {
    configureChamberMessagelabel = "configureChamberMessagelabel",
    configureChamberLink = "configureChamberLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.configureChamberMessagelabel]: string;
        [fields.configureChamberLink]: string;
    };
}

type Props = OwnProps & WidgetProps & HasProduct;

export interface ProductDetailsConfigureChamberStyles {
    configWrapper?: InjectableCss;
    configButton?: InjectableCss;
    iconWrapper?: InjectableCss;
    buttonText?: TypographyProps;
}

const configureChamberMessageStyles: ProductDetailsConfigureChamberStyles = {
    configWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            width: 100%;
            background-color: #f7F9FA;

            @media (min-width: 1024px) {
                margin-top: 1rem;
                padding: .7rem 1.5rem;
            }
        `,
    },
    configButton: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            height: 40px;
            border: 1px solid #dcdcdc;
            background-color: #ffffff;
            cursor: pointer;
            box-sizing: border-box;
            border-radius: 4px;           
            justify-content: center;
        `,
    },
    iconWrapper: {
        css: css`
            width: 30px;
            height: 30px;
            margin: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    buttonText: {
        css: css`
            font-family: 'Lato', sans-serif;
            font-weight: 700;
            font-size: 13px;
            leading-trim: Cap height;
            line-height: 100%;
            letter-spacing: 0%;
            color: #333F47;
        `,
    },

};

const styles = configureChamberMessageStyles;

const ProductDetailsConfigureChamber: FC<Props> = ({ fields, product }) => {
    let featureFlags: string[] = ["TEUS-8610"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const isFeatureTEUS8610On = flagValues["TEUS-8610"];

    if (!product) {
        return null;
    }

    return (
        <>
            {isFeatureTEUS8610On && product && product.brand?.name?.toLowerCase().includes("testequity chambers") &&
                <StyledWrapper {...styles.configWrapper}>
                    <StyledWrapper
                        {...styles.configButton}
                        id="configureChamberButton"
                        data-test-selector="ConfigureChamberButton"
                        onClick={() => {
                            window.location.href = fields.configureChamberLink;
                        }}
                    >
                        <StyledWrapper {...styles.iconWrapper}>
                            <Icon src={GearIcon} />
                        </StyledWrapper>
                        <Typography {...styles.buttonText}>
                            {fields.configureChamberMessagelabel || translate("Configure your chamber!")}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            }
        </>

    );
};

const widgetModule: WidgetModule = {
    component: connect()(withProduct(ProductDetailsConfigureChamber)),
    definition: {
        displayName: "Configure your chamber",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [
            {
                name: fields.configureChamberMessagelabel,
                editorTemplate: "TextField",
                defaultValue: "Configure your chamber!",
                fieldType: "Translatable",
            },
            {
                name: fields.configureChamberLink,
                editorTemplate: "TextField",
                defaultValue: "https://www.testequity.com/chambers-configurator",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;

